import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, spendCrumbs, setFlag, getFlag, getBankBalance, depositBank, withdrawBank, claimBankInterest, peekBankInterest, getState } from '../core/save.js';
import { questsForGiver, acceptQuest, turnIn, isReady as isQuestReady, objectiveProgress, QUESTS, flagObjective } from '../core/quests.js';

// The Bread Bank — a secure little branch in downtown. Open a savings account,
// make a deposit, and earn interest back from Teller Toast.

export function createBankScene(){
  return () => new BankScene();
}

const DEPOSIT = 50;

class BankScene {
  constructor(){
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xE8E0D0);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 11;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.45;

    this._buildRoom();
    this._buildVault();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 3.2);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.teller = this._makeTeller();
    this.scene.add(this.teller.root);

    this.hotspots = [
      { pos: this.teller.root.position.clone(), r: 2.3, label: '🗣 talk to Teller Toast', action: () => this._talkToTeller() },
      { pos: new THREE.Vector3(0, 0, 5.4), r: 2.2, label: '← back to downtown', action: () => this.manager.goTo('downtown', { from: 'bank' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.8));
    const key = new THREE.DirectionalLight(0xFFF6E8, 0.5);
    key.position.set(-3, 10, 5); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.MeshLambertMaterial({ color: 0xC0B8A8 }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);

    const wallMat = new THREE.MeshLambertMaterial({ color: 0xE0D8C8 });
    const back = new THREE.Mesh(new THREE.PlaneGeometry(14, 8), wallMat);
    back.position.set(0, 4, -6); back.receiveShadow = true; this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-7, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(7, 4, 0); this.scene.add(sideR);

    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 5.9, this.scene);
    const sign = textSprite('← to Downtown', 26, '#6B4A2F', 2.8, 0.8);
    sign.position.set(0, 2.9, 5.7); this.scene.add(sign);

    // teller counter
    const counter = new THREE.Group(); counter.position.set(-2.8, 0, -1.5); this.scene.add(counter);
    box(2.6, 1.0, 1.0, 0x6E4A35, 0, .5, 0, counter);
    box(2.7, .12, 1.1, 0x8A7A6A, 0, 1.02, 0, counter);
    // queue ropes
    cyl(.04, .04, .8, 0x8A2E2E, -1.0, .4, 2.0, this.scene, 6);
    cyl(.04, .04, .8, 0x8A2E2E,  1.0, .4, 2.0, this.scene, 6);
    sphere(.12, 0xFFD700, -1.0, .86, 2.0, this.scene, 8);
    sphere(.12, 0xFFD700,  1.0, .86, 2.0, this.scene, 8);
  }

  _buildVault(){
    const g = new THREE.Group(); g.position.set(3.8, 0, -3.2); this.scene.add(g);
    box(3.0, 3.2, 1.0, 0x555555, 0, 1.6, 0, g);          // vault frame
    box(2.2, 2.6, .15, 0x777777, 0, 1.3, .5, g);          // door
    cyl(.18, .18, .2, 0x333333, .7, 1.3, .6, g, 12);      // wheel
    // spokes
    for (let i = 0; i < 4; i++){
      const s = box(.04, .28, .04, 0x222222, .7, 1.3, .62, g);
      s.rotation.z = i * Math.PI / 4;
    }
  }

  _makeTeller(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.6, 1.1);
      sphere(.07, 0x2E2018, -.16, 1.18, .3, g, 8);
      sphere(.07, 0x2E2018,  .16, 1.18, .3, g, 8);
      // visor
      box(.66, .12, .3, 0x3D5FC0, 0, 1.62, .12, g);
      // bow tie
      box(.3, .12, .08, 0xE23B4E, 0, .58, .36, g);
      // suit
      box(.62, .7, .38, 0x3A5A8A, 0, .45, 0, g);
      box(.16, .7, .4, 0x3A5A8A, -.24, .45, 0, g);
      box(.16, .7, .4, 0x3A5A8A,  .24, .45, 0, g);
      stickLegs(g, .5);
    };
    return new Townsperson({
      name: 'Teller Toast', line: 'your crumbs are safe with us.',
      pos: new THREE.Vector3(-2.8, 0, -.7), facing: Math.PI * .75,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _talkToTeller(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.teller.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderTellerDialog();
  }

  _renderTellerDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('teller');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isQuestReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const body = questRows.length ? questRows.join('') : '<p class="q-dnone">"Thanks for banking with us. Your crumbs are earning interest as we speak."</p>';

    const balance = getBankBalance();
    const day = getState().world.day;
    const canDeposit = getCrumbs() >= DEPOSIT && !getFlag('madeBankDeposit');
    const canWithdraw = balance > 0 && !getFlag('madeBankWithdraw');
    const interest = peekBankInterest(day, 0.05);
    const canClaim = interest > 0 && !getFlag('claimedBankInterest');

    const depositRow = `
      <div class="q-drow"><div><b>🏦 Open a savings account</b><span>Deposit 🍞 ${DEPOSIT} to start earning interest.</span></div>
        <button class="cafe-btn deposit ${canDeposit ? '' : 'disabled'}">${canDeposit ? `deposit 🍞 ${DEPOSIT}` : (getFlag('madeBankDeposit') ? 'account open' : 'need crumbs')}</button></div>`;
    const withdrawRow = `
      <div class="q-drow"><div><b>💰 Withdraw savings</b><span>Balance: 🍞 ${balance}</span></div>
        <button class="cafe-btn withdraw ${canWithdraw ? '' : 'disabled'}">${canWithdraw ? 'withdraw all' : (balance > 0 ? 'quest done' : 'empty')}</button></div>`;
    const interestRow = `
      <div class="q-drow"><div><b>📈 Collect interest</b><span>${canClaim ? `🍞 ${interest} ready` : 'check back tomorrow'}</span></div>
        <button class="cafe-btn interest ${canClaim ? '' : 'disabled'}">${canClaim ? `claim 🍞 ${interest}` : 'no interest yet'}</button></div>`;

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Teller Toast</h2>
        <div class="cafe-sub">the bread bank · balance 🍞 ${balance}</div>
        <div class="q-drows">${body}${depositRow}${withdrawRow}${interestRow}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderTellerDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onTurnIn(b.dataset.id));
    p.querySelector('.deposit')?.addEventListener('click', () => this._makeDeposit());
    p.querySelector('.withdraw')?.addEventListener('click', () => this._makeWithdraw());
    p.querySelector('.interest')?.addEventListener('click', () => this._claimInterest());
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _makeDeposit(){
    if (getCrumbs() < DEPOSIT || getFlag('madeBankDeposit')) return;
    depositBank(DEPOSIT);
    setFlag('madeBankDeposit', true);
    flagObjective('bank-deposit', 'madeBankDeposit');
    HUD.refreshCrumbs();
    this._renderTellerDialog();
    HUD.say(`Deposited ${DEPOSIT} crumbs. The account is open!`);
  }

  _makeWithdraw(){
    const balance = getBankBalance();
    const amount = Math.min(balance, 25);
    if (amount <= 0 || getFlag('madeBankWithdraw')) return;
    withdrawBank(amount);
    setFlag('madeBankWithdraw', true);
    flagObjective('bank-withdraw', 'madeBankWithdraw');
    HUD.refreshCrumbs();
    this._renderTellerDialog();
    HUD.say(`Withdrew ${amount} crumbs.`);
  }

  _claimInterest(){
    const day = getState().world.day;
    const interest = claimBankInterest(day, 0.05);
    if (interest <= 0) return;
    setFlag('claimedBankInterest', true);
    flagObjective('bank-interest', 'claimedBankInterest');
    HUD.refreshCrumbs();
    this._renderTellerDialog();
    HUD.say(`Collected ${interest} crumbs in interest.`);
  }

  _onTurnIn(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    this.bread.drawFace('yum');
    this._renderTellerDialog();
  }

  _panel(html, mode = 'panel'){
    const el = document.createElement('div');
    el.className = mode === 'bare' ? 'cafe-panel bare' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('the bread bank');
    HUD.say('The Bread Bank. Talk to Teller Toast about opening an account.');
    HUD.setHint('click to walk · talk to Teller Toast or head back to downtown');
    HUD.hideHintSoon(7000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, this.teller, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6, Math.min(6, gp.x));
    gp.z = Math.max(-5, Math.min(5.5, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    this.teller.update(dt, this.bread.root.position);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
